import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { API_URL_COMPANIES, API_URL_PREFIX } from 'src/app/shared/services/basic.service';
import GenericUtils from 'src/app/shared/utils/generic.utils';

import { ImageInfos } from '../models';

@Injectable({
  providedIn: 'root',
})
export class MediasService {
  baseUrl = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}`;
  compananyBaseUrl = `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}`;
  sitesBaseUrl = `${this.compananyBaseUrl}/sites/`;

  constructor(private alcmeonContextService: AlcmeonContextService, private asyncRequestService: AsyncRequestService) {}

  /**
   * Download a file after adding a short lived jwt to its url
   */
  downloadFile(fileUrl: string, fileName?: string) {
    this.alcmeonContextService.addJwtShortToUrl(fileUrl).subscribe((urlWithJwt: string) => {
      // it's the only way, in full JS, to force download a file the browser could display itself (like .txt)
      // otherwise it must be in HTML with "download" attribute, or server side
      // (open a new tab in localhost and do not download, but ok when front is on server)
      const a = document.createElement('a');
      a.href = urlWithJwt;
      a.download = fileName || '0';
      a.click();
    });
  }

  downloadBinaryBase64(content, content_type, content_filename) {
    const bytes = Uint8Array.from(window.atob(content), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: content_type });
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = content_filename;
    element.click();
  }

  getPictureUrl(pictureId: number): string {
    if (pictureId) {
      return `${this.baseUrl}/pictures/${pictureId}`;
    }
    return '';
  }

  /**
   * @deprecated Prefer to use checkLimitationsAndUpload instead and pass the options through the first param object
   */
  upload2(siteId: number, mediaType: string, file: any, ok: any, fail: any, progress: any) {
    let endpoint = `${this.sitesBaseUrl}${siteId}/medias`;
    if (mediaType === 'file') {
      endpoint = `${this.sitesBaseUrl}${siteId}/file-upload`;
    }

    this.doUpload(mediaType, file, endpoint, this.alcmeonContextService.jwt, ok, fail, progress);
  }

  /**
   * Allows to upload the given file only if the media limitations are respected.
   * The allowed options are siteId, mediaType, file, mediaLimitations.
   * @param {*} ok the function callback in case of success.
   * @param {*} fail  the function callback in case of error.
   * @param {*} progress  the function callback while performing the action.
   */
  checkLimitationsAndUpload(options, ok, fail, progress?) {
    const { siteId, mediaType, file, mediaLimitations, uploadAsync } = options;
    try {
      this.checkLimitations(mediaLimitations, mediaType, file);
      if (uploadAsync) {
        this.uploadAsync(siteId, mediaType, file).subscribe({
          next: response => {
            // match doUpload XMLHttpRequest reponse
            ok({ status: 200, data: response });
          },
          error: ({ status, error }) => {
            fail({ status, data: { error } });
          },
        });
      } else {
        this.upload2(siteId, mediaType, file, ok, fail, progress);
      }
    } catch (error) {
      fail({ data: { error: error.translationPath, translationData: error.translationData } });
      return false;
    }
  }

  private checkLimitations(mediaLimitations, mediaType: string, file) {
    if (!mediaLimitations) {
      return;
    }
    const { size, type } = file;

    const checkWeight = weight => {
      if (weight) {
        const maxWeight = (weight || 0) * 1024 * 1024;
        if (maxWeight && size > maxWeight) {
          const translationPath = 'media/upload/too-big/limitation';
          const translationData = { max_size: weight };
          throw { translationPath, translationData };
        }
      }
    };

    const checkSupportedTypes = supportedTypes => {
      if (!['video/*', 'image/*'].includes(supportedTypes)) {
        const supportedContentTypes = supportedTypes?.split(',');
        if (supportedContentTypes?.length && !supportedContentTypes.includes(type)) {
          const translationPath = 'media/upload/wrong-format/limitation';
          const translationData = { supported_types: supportedTypes };
          throw { translationPath, translationData };
        }
      }
    };

    if (mediaType === 'image') {
      checkSupportedTypes(mediaLimitations.imageMimeTypes);
      checkWeight(mediaLimitations.imageMaxSizeMb);
    } else if (mediaType === 'video') {
      checkSupportedTypes(mediaLimitations.videoSupportedTypes);
      checkWeight(mediaLimitations.videoMaxSizeMb);
    } else if (mediaType === 'file') {
      checkSupportedTypes(mediaLimitations.fileMediaTypes);
      checkWeight(mediaLimitations.fileMaxSizeMb);
    }
  }

  private doUpload(mediaType: string, file, endpoint: string, jwt: string, ok, fail, progress) {
    // We do not use $http because we can't get progress updates from $http
    const fd = new FormData();
    fd.append('media_type', mediaType);
    fd.append('file_name', file.name);
    fd.append('file', file);
    const request = new XMLHttpRequest();

    if (progress) {
      request.upload.onprogress = function (e) {
        const percent = (e.loaded / e.total) * 100;
        progress(percent);
      };
    }

    request.onload = function () {
      const resp = JSON.parse(request.response);
      if (request.status === 200) {
        ok({ status: request.status, data: resp });
      } else {
        fail({ status: request.status, data: resp });
      }
    };

    request.onerror = function () {
      fail({ status: -1, data: { error: 'media/upload/unexpected-error' } });
    };

    request.open('POST', endpoint, true);
    request.setRequestHeader('Authorization', `Bearer ${jwt}`);
    request.send(fd);
    return request;
  }

  /**
   * TODO pass headers in param of asyncRequestService.post
   */
  private uploadAsync(siteId: number, mediaType: string, file) {
    const url = `${this.sitesBaseUrl}${siteId}/scenarios/medias`;

    const fd = new FormData();
    fd.append('media_type', mediaType);
    fd.append('file_name', file.name);
    fd.append('file', file);

    // TODO pass headers in param, doesn't work at first sight (asyncRequestService.post was modified to accept headers)
    // but can't confirm as long as "question-edition.directive.js" is not migrated
    // const headers = { 'Content-Type': undefined }; // Alows to disable the content type application/json and let the transaction defines form data multipart.
    return this.asyncRequestService.post(url, fd /*, headers*/);
  }

  /**
   * Get image base64 string and dimensions
   * Use FileReader and create new Image object, which are async.
   * Encapsulate everything in an Observable to be more convenient to use.
   *
   * @param file File
   * @returns Observable
   */
  getImageInfo(file: any): Observable<ImageInfos> {
    const imageInfos: ImageInfos = { b64Url: null, height: null, width: null };

    const observable$ = new Observable<ImageInfos>(observer => {
      if (file && GenericUtils.realTypeOf(file) === 'file') {
        const reader = new FileReader();
        reader.onload = event => {
          imageInfos.b64Url = event.target.result as string;

          const imageObj = new Image();

          imageObj.onload = () => {
            imageInfos.height = imageObj.height;
            imageInfos.width = imageObj.width;
            observer.next(imageInfos);
            observer.complete();
          };

          imageObj.onerror = error => {
            observer.error(error);
          };

          imageObj.src = event.target.result as string;
        };

        reader.onerror = error => {
          observer.error(error);
        };

        reader.readAsDataURL(file);
      } else {
        observer.error(true);
      }
    });

    return observable$;
  }
}
